import { useState, useEffect } from 'react'
import { Gift, UserPlus, X, PartyPopper } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { AnimatePresence, motion } from 'framer-motion'
import { apiGetJson } from '../../../lib/apiClient'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const BANNER_DISMISSED_KEY = 'digigo_referral_banner_dismissed_session'

interface ReferralBannerProps {
    onOpenAuth: () => void
}

interface ReferrerInfo {
    full_name: string
    username: string
}

export default function ReferralBanner({ onOpenAuth }: ReferralBannerProps) {
    const { user, isInitializing } = useAuth()
    const [referrerId, setReferrerId] = useState<string | null>(null)
    const [referrerInfo, setReferrerInfo] = useState<ReferrerInfo | null>(null)
    const [visible, setVisible] = useState(false)
    const [dismissed, setDismissed] = useState(false)

    // Detect referrer_id from URL params
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search)
            const candidate = (params.get('referrer_id') || params.get('ref') || '').trim()

            if (candidate && UUID_REGEX.test(candidate)) {
                // Check sessionStorage — only dismiss for this browser session
                const dismissedInSession = sessionStorage.getItem(BANNER_DISMISSED_KEY)
                if (dismissedInSession === candidate) return

                setReferrerId(candidate)
                setVisible(true)
            }
        } catch (e) {
            console.warn('ReferralBanner: Failed to read referrer', e)
        }
    }, [])

    // Fetch referrer info from backend
    useEffect(() => {
        if (!referrerId) return

        const fetchInfo = async () => {
            try {
                const resp = await apiGetJson<any>(`/api/auth/referrer-info?id=${referrerId}`)
                if (resp?.data && (resp.data.full_name || resp.data.username)) {
                    setReferrerInfo(resp.data)
                }
            } catch (e) {
                console.warn('Failed to fetch referrer info', e)
            }
        }
        fetchInfo()
    }, [referrerId])

    const handleDismiss = () => {
        setDismissed(true)
        setTimeout(() => setVisible(false), 300)
        if (referrerId) {
            try {
                sessionStorage.setItem(BANNER_DISMISSED_KEY, referrerId)
            } catch (e) { /* ignore */ }
        }
    }

    const handleRegister = () => {
        handleDismiss()
        onOpenAuth()
    }

    const handleLogin = () => {
        handleDismiss()
        // Don't open auth - just dismiss. They can click login in header.
    }

    // Don't show while initializing
    if (isInitializing) return null
    if (!visible || !referrerId) return null

    const isLoggedIn = !!user
    const referrerDisplayName = referrerInfo?.full_name || referrerInfo?.username || null

    return (
        <AnimatePresence>
            {!dismissed && (
                <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                >
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleDismiss}
                    />

                    {/* Modal content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
                        className="relative w-full max-w-md bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-slate-800 dark:via-slate-900 dark:to-amber-950/30 rounded-2xl shadow-2xl border border-amber-200/60 dark:border-amber-700/30 overflow-hidden"
                    >
                        {/* Decorative top gradient */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600" />

                        {/* Close button */}
                        <button
                            onClick={handleDismiss}
                            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors z-10"
                        >
                            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        </button>

                        <div className="p-6 pt-7">
                            {isLoggedIn ? (
                                /* Already logged in */
                                <div className="text-center space-y-4">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/40 mb-2">
                                        <PartyPopper className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                            Bạn đã có tài khoản rồi! 🎉
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                                            Cảm ơn bạn đã sử dụng DigiGO. Link giới thiệu này dành cho người chưa có tài khoản.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleDismiss}
                                        className="w-full py-2.5 px-4 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl transition-colors"
                                    >
                                        Đã hiểu
                                    </button>
                                </div>
                            ) : (
                                /* Not logged in — show referral benefits + register CTA */
                                <div className="space-y-5">
                                    <div className="text-center">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mb-3 shadow-lg shadow-amber-400/30">
                                            <Gift className="h-8 w-8 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                            Bạn được giới thiệu! 🎁
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            {referrerDisplayName
                                                ? <><strong className="text-amber-600 dark:text-amber-400">{referrerDisplayName}</strong> đã chia sẻ link DigiGO cho bạn</>
                                                : 'Ai đó đã chia sẻ link DigiGO cho bạn'
                                            }
                                        </p>
                                    </div>

                                    {/* Referrer code display */}
                                    <div className="bg-amber-50/80 dark:bg-amber-900/20 rounded-lg px-3 py-2 flex items-center justify-between border border-amber-200/50 dark:border-amber-700/30">
                                        <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">Mã giới thiệu:</span>
                                        <code className="text-xs font-mono text-amber-800 dark:text-amber-300 bg-white/60 dark:bg-slate-800/60 px-2 py-0.5 rounded">
                                            {referrerId.substring(0, 8).toUpperCase()}
                                        </code>
                                    </div>

                                    {/* Benefits */}
                                    <div className="bg-white/70 dark:bg-slate-800/70 rounded-xl p-4 space-y-3 border border-amber-100 dark:border-amber-800/30">
                                        <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                            <Gift className="h-4 w-4" />
                                            Ưu đãi khi đăng ký qua link giới thiệu:
                                        </p>
                                        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                            <li className="flex items-start gap-2">
                                                <span className="text-amber-500 mt-0.5">✦</span>
                                                <span>Được <strong className="text-amber-600 dark:text-amber-400">kế thừa 15% tổng nạp</strong> của người giới thiệu — giúp thăng hạng nhanh hơn</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-amber-500 mt-0.5">✦</span>
                                                <span>Thăng hạng nhanh = <strong className="text-amber-600 dark:text-amber-400">được giảm giá nhiều hơn</strong> khi mua sản phẩm</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-amber-500 mt-0.5">✦</span>
                                                <span>Hỗ trợ ưu tiên từ <strong className="text-amber-600 dark:text-amber-400">người giới thiệu</strong></span>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* CTA */}
                                    <button
                                        onClick={handleRegister}
                                        className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25 active:scale-[0.98]"
                                    >
                                        <UserPlus className="h-5 w-5" />
                                        Đăng ký ngay để nhận ưu đãi
                                    </button>

                                    <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                                        Đã có tài khoản?{' '}
                                        <button onClick={handleLogin} className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
                                            Đăng nhập
                                        </button>
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
